// Staff classes view - class management

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Pencil icon SVG
const pencilIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path
    d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"
  />
</svg>`;

// Trash icon SVG
const trashIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M3 6h18" />
  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
</svg>`;

// Clipboard icon SVG
const clipboardIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
</svg>`;

/**
 * Staff classes view component for managing classes
 */
class StaffClassesView extends BaseComponent {
  static properties = {
    _classes: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _showDeleteModal: { type: Boolean, state: true },
    _classToDelete: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').Class>} */
    this._classes = [];
    this._loading = true;
    this._error = null;
    this._showDeleteModal = false;
    this._classToDelete = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadClasses();
  }

  async _loadClasses() {
    this._loading = true;
    this._error = null;

    try {
      this._classes = await api.get('/api/staff/classes');
    } catch (err) {
      console.error('Failed to load classes:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load classes';
    } finally {
      this._loading = false;
    }
  }

  _goToNewClass() {
    router.navigate('/staff/classes/new');
  }

  /** @param {string} slug */
  _editClass(slug) {
    router.navigate(`/staff/classes/${slug}/edit`);
  }

  /** @param {string} slug */
  _deleteClass(slug) {
    this._classToDelete = slug;
    this._showDeleteModal = true;
  }

  async _confirmDelete() {
    if (!this._classToDelete) return;

    try {
      await api.delete(`/api/staff/classes/${this._classToDelete}`);
      this._classToDelete = null;
      this._showDeleteModal = false;
      await this._loadClasses();
    } catch (err) {
      console.error('Failed to delete class:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to delete class';
    }
  }

  _cancelDelete() {
    this._showDeleteModal = false;
    this._classToDelete = null;
  }

  /**
   * @param {string} description
   * @param {number} [maxLength]
   */
  _truncateDescription(description, maxLength = 100) {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}...`;
  }

  /**
   * @param {string} text
   * @param {Event} event
   */
  _copyToClipboard(text, event) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('button');
        if (btn) {
          btn.classList.add('btn-success');
          btn.classList.remove('btn-outline-secondary');
          setTimeout(() => {
            btn.classList.remove('btn-success');
            btn.classList.add('btn-outline-secondary');
          }, 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to copy:', err);
      });
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item active" aria-current="page">Classes</li>
        </ol>
      </nav>

      ${this._error ? html` <div class="alert alert-danger">${this._error}</div> ` : ''}

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2>Classes</h2>
        <button class="btn btn-primary" @click="${this._goToNewClass}">Create Class</button>
      </div>

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          `
          : this._classes.length === 0
            ? html` <p class="text-muted">No classes yet.</p> `
            : html`
              <div class="d-flex flex-wrap gap-3">
                ${this._classes.map(
                  (cls) => html`
                    <div style="flex: 1 1 280px; min-width: 260px; max-width: 320px;">
                      <div class="card h-100">
                        <div class="card-body">
                          <div class="d-flex justify-content-between align-items-start mb-2">
                            <h6 class="card-title mb-0">${cls.name}</h6>
                            <div class="d-flex gap-1">
                              <button
                                class="btn btn-sm btn-outline-primary"
                                title="Edit"
                                @click="${() => this._editClass(cls.slug)}"
                              >
                                ${pencilIcon}
                              </button>
                              <button
                                class="btn btn-sm btn-outline-danger"
                                title="Delete"
                                @click="${() => this._deleteClass(cls.slug)}"
                              >
                                ${trashIcon}
                              </button>
                            </div>
                          </div>

                          <div class="d-flex align-items-center gap-2 mb-2">
                            <code class="small text-muted">${cls.slug}</code>
                            <button
                              class="btn btn-sm btn-outline-secondary py-0 px-2"
                              title="Copy Slug"
                              @click="${(/** @type {Event} */ e) => this._copyToClipboard(cls.slug, e)}"
                            >
                              ${clipboardIcon}
                            </button>
                          </div>

                          <div class="mb-2">
                            <div class="d-flex gap-1 mb-2">
                              <span class="badge bg-secondary text-capitalize">${cls.complexity}</span>
                              <span class="badge bg-secondary">v${cls.version || 1}</span>
                              ${
                                cls.published
                                  ? html` <span class="badge bg-success">Published</span> `
                                  : html` <span class="badge bg-warning text-dark">Draft</span> `
                              }
                            </div>
                          </div>

                          ${
                            cls.description
                              ? html` <p class="small text-muted mb-2">${this._truncateDescription(cls.description)}</p> `
                              : ''
                          }

                          <div class="small">
                            <div><strong>Likes:</strong> ${cls.likes}</div>
                            <div><strong>Primary Ability:</strong> ${cls.primaryAbility}</div>
                            <div><strong>Hit Die:</strong> d${cls.hitDie}</div>
                            ${
                              cls.spellcastingAbility
                                ? html`
                                  <div><strong>Spellcasting:</strong> ${cls.spellcastingAbility.toUpperCase()}</div>
                                `
                                : ''
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
      }

      <!-- Delete Confirmation Modal -->
      ${
        this._showDeleteModal
          ? html`
            <div class="modal show d-block" style="background: rgba(0,0,0,0.5);">
              <div class="modal-dialog">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">Delete Class</h5>
                    <button type="button" class="btn-close" @click="${this._cancelDelete}"></button>
                  </div>
                  <div class="modal-body">
                    <p>Are you sure you want to delete this class? This cannot be undone.</p>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="${this._cancelDelete}">Cancel</button>
                    <button type="button" class="btn btn-danger" @click="${this._confirmDelete}">Delete</button>
                  </div>
                </div>
              </div>
            </div>
          `
          : ''
      }
    `;
  }
}

customElements.define('staff-classes-view', StaffClassesView);
